/**
 * Messages Query Hooks
 *
 * Reusable TanStack Query hooks for chat/messaging operations using the CC Platform SDK.
 * Handles chat groups list, message loading with cursor pagination, and cache management.
 */

import { useQuery, useQueryClient, useMutation } from "@tanstack/vue-query";
import { computed, type ComputedRef, type Ref } from "vue";
import { useSdk } from "@/composables/useSdk";
import { useInfiniteFeedList } from "@/composables/useInfiniteFeedList";
import type { ChatGroup, ChatMessage } from "@cc-consulting-nv/ccsdk";
import { queryKeys } from "./queryKeys";

/**
 * Messages response structure from API
 */
interface MessagesResponse {
  messages: ChatMessage[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

/**
 * Chat groups query result
 */
export interface ChatGroupsResult {
  groups: ComputedRef<ChatGroup[]>;
  isLoading: ComputedRef<boolean>;
  isFetching: ComputedRef<boolean>;
  error: ComputedRef<Error | null>;
  refetch: () => void;
}

/**
 * Messages infinite query result
 */
export interface MessagesResult {
  messages: ComputedRef<ChatMessage[]>;
  isLoading: ComputedRef<boolean>;
  isFetching: ComputedRef<boolean>;
  isFetchingNextPage: ComputedRef<boolean>;
  hasNextPage: ComputedRef<boolean>;
  error: ComputedRef<Error | null>;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Hook for fetching chat groups list
 *
 * @param options - Optional configuration
 * @example
 * const { groups, isLoading, refetch } = useChatGroups()
 */
export function useChatGroups(
  options: { enabled?: ComputedRef<boolean> | boolean } = {},
): ChatGroupsResult {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const query = useQuery({
    queryKey: queryKeys.messages.groups(),
    enabled: enabledValue,
    queryFn: async () => {
      const response = await sdk.getChatGroups();
      return response.data || [];
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
    // No polling - unread counts are updated via real-time WebSocket events (ChatUnreadCountUpdated)
    // The user subscribes to their private channel (user-{ulid}) in App.vue
  });

  return {
    groups: computed(() => query.data.value ?? []),
    isLoading: computed(() => query.isLoading.value),
    isFetching: computed(() => query.isFetching.value),
    error: computed(() => query.error.value),
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching messages in a conversation with infinite scroll
 *
 * @param groupUlid - The chat group's ULID
 * @param options - Optional configuration
 * @example
 * const { messages, fetchNextPage, hasNextPage } = useConversationMessages(groupUlid)
 */
export function useConversationMessages(
  groupUlid: ComputedRef<string | null> | Ref<string | null>,
  options: { enabled?: ComputedRef<boolean> | boolean } = {},
): MessagesResult {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const groupUlidValue = computed(() =>
    typeof groupUlid === "object" && "value" in groupUlid
      ? groupUlid.value
      : groupUlid,
  );

  const enabledValue = computed(() => {
    const baseEnabled = typeof enabled === "object" ? enabled.value : enabled;
    return baseEnabled && !!groupUlidValue.value;
  });

  const {
    items: messages,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteFeedList<MessagesResponse, ChatMessage, string | undefined>({
    queryKey: computed(() =>
      queryKeys.messages.conversation(groupUlidValue.value || ""),
    ),
    enabled: enabledValue,
    queryFn: async ({ pageParam }): Promise<MessagesResponse> => {
      if (!groupUlidValue.value) {
        return { messages: [], nextCursor: undefined };
      }

      const response = (await sdk.getChatMessages(groupUlidValue.value, {
        cursor: pageParam,
      })) as unknown;

      // Handle various response formats
      const responseObj = response as
        | Record<string, unknown>
        | unknown[]
        | null;
      let rawMessages: unknown[] = [];

      if (responseObj && !Array.isArray(responseObj)) {
        const obj = responseObj as Record<string, unknown>;
        if (obj.messages && Array.isArray(obj.messages)) {
          rawMessages = obj.messages;
        } else if (obj.data && Array.isArray(obj.data)) {
          rawMessages = obj.data;
        } else if (obj.data && typeof obj.data === "object") {
          const innerData = obj.data as Record<string, unknown>;
          if (innerData.data && Array.isArray(innerData.data)) {
            rawMessages = innerData.data;
          } else if (innerData.messages && Array.isArray(innerData.messages)) {
            rawMessages = innerData.messages;
          }
        }
      } else if (Array.isArray(responseObj)) {
        rawMessages = responseObj;
      }

      // Transform messages
      const messages = rawMessages
        .filter(
          (m): m is Record<string, unknown> =>
            m != null &&
            ((m as Record<string, unknown>).id != null ||
              (m as Record<string, unknown>).ulid != null),
        )
        .map((m): ChatMessage => {
          const apiMsg = m as Record<string, unknown>;
          const user = apiMsg.user as Record<string, unknown> | undefined;

          return {
            id: String(apiMsg.id || apiMsg.ulid),
            ulid: apiMsg.ulid as string | undefined,
            groupUlid: String(apiMsg.groupId || groupUlidValue.value),
            senderId: String(user?.userId || apiMsg.userId || ""),
            senderUlid: user?.userId as string | undefined,
            sender: user
              ? {
                  ulid: String(user.userId || ""),
                  username: (apiMsg.username as string) || "",
                  name: apiMsg.username as string,
                  avatar: user.avatar as string | undefined,
                }
              : undefined,
            body: (apiMsg.body as string) || "",
            createdAt: apiMsg.createdAt as string | undefined,
            readAt: apiMsg.readAt as string | null | undefined,
          };
        });

      // Extract cursor from response
      const cursor =
        responseObj && !Array.isArray(responseObj)
          ? ((responseObj as Record<string, unknown>).next_cursor as
              | string
              | undefined) ||
            ((responseObj as Record<string, unknown>).nextCursor as
              | string
              | undefined)
          : undefined;

      return {
        messages,
        nextCursor: cursor,
        hasMore: messages.length > 0 && !!cursor,
      };
    },
    selectItems: (page) => page.messages,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 0, // Always refetch messages for real-time feel
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    messages,
    isLoading: computed(() => isLoading.value),
    isFetching: computed(() => isFetching.value),
    isFetchingNextPage: computed(() => isFetchingNextPage.value),
    hasNextPage: computed(() => hasNextPage.value ?? false),
    error: computed(() => error.value as Error | null),
    fetchNextPage,
    refetch,
  };
}

/**
 * Hook for sending a message
 *
 * @example
 * const { mutate: sendMessage, isPending } = useSendMessage()
 * sendMessage({ groupUlid: '...', body: 'Hello!', senderUlid: '...' })
 */
export function useSendMessage() {
  const { sdk } = useSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupUlid,
      body,
      senderUlid,
      attachments,
    }: {
      groupUlid: string;
      body: string;
      senderUlid: string;
      attachments?: { url: string; type: "image" | "video" }[];
    }) => {
      const payload: Record<string, unknown> = { body };
      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }
      const response = await sdk.sendChatMessage(groupUlid, payload);

      // Handle different response formats
      let sentMessage: ChatMessage | undefined;
      if (response?.data && typeof response.data === "object") {
        sentMessage = response.data as ChatMessage;
      } else if (
        response &&
        typeof response === "object" &&
        ("id" in response || "ulid" in response)
      ) {
        sentMessage = response as unknown as ChatMessage;
      }

      // Ensure senderId is set correctly for own message detection
      if (sentMessage) {
        sentMessage.senderId = senderUlid;
        sentMessage.senderUlid = senderUlid;
        // Mark as read since it's our own message
        sentMessage.readAt = new Date().toISOString();
      }

      return { groupUlid, message: sentMessage };
    },
    onSuccess: ({ groupUlid, message }) => {
      // Optimistically update the conversation cache
      if (message) {
        queryClient.setQueryData(
          queryKeys.messages.conversation(groupUlid),
          (
            oldData:
              | { pages: MessagesResponse[]; pageParams: unknown[] }
              | undefined,
          ) => {
            if (!oldData) return oldData;

            // Add the new message to the last page (chronological order)
            const newPages = [...oldData.pages];
            const lastPageIndex = newPages.length - 1;
            if (lastPageIndex >= 0) {
              newPages[lastPageIndex] = {
                ...newPages[lastPageIndex],
                messages: [...newPages[lastPageIndex].messages, message],
              };
            }

            return { ...oldData, pages: newPages };
          },
        );
      }

      // Invalidate chat groups to update last message
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.groups() });
    },
  });
}

/**
 * Hook for marking a chat group as read
 *
 * @example
 * const { mutate: markRead } = useMarkChatRead()
 * markRead(groupUlid)
 */
export function useMarkChatRead() {
  const { sdk } = useSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupUlid: string) => {
      await sdk.markChatGroupRead(groupUlid);
      return groupUlid;
    },
    onSuccess: (groupUlid) => {
      // Update the groups cache to set unread count to 0
      queryClient.setQueryData(
        queryKeys.messages.groups(),
        (oldData: ChatGroup[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((group) =>
            group.ulid === groupUlid ? { ...group, unreadCount: 0 } : group,
          );
        },
      );
    },
  });
}

/**
 * Add a message to the conversation cache (for real-time updates)
 */
export function addMessageToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  groupUlid: string,
  message: ChatMessage,
) {
  const queryKey = queryKeys.messages.conversation(groupUlid);

  queryClient.setQueryData(
    queryKey,
    (
      oldData: { pages: MessagesResponse[]; pageParams: unknown[] } | undefined,
    ) => {
      // If no existing data, create initial structure
      if (!oldData || !oldData.pages || oldData.pages.length === 0) {
        return {
          pages: [
            { messages: [message], nextCursor: undefined, hasMore: false },
          ],
          pageParams: [undefined],
        };
      }

      // Check if message already exists
      const allMessages = oldData.pages.flatMap((p) => p.messages);
      const exists = allMessages.some(
        (m) =>
          String(m.id) === String(message.id) ||
          String(m.ulid) === String(message.ulid),
      );
      if (exists) {
        return oldData;
      }

      // Add the new message to the last page (messages are chronological)
      const newPages = [...oldData.pages];
      const lastPageIndex = newPages.length - 1;
      newPages[lastPageIndex] = {
        ...newPages[lastPageIndex],
        messages: [...newPages[lastPageIndex].messages, message],
      };

      return { ...oldData, pages: newPages };
    },
  );
}

/**
 * Update a chat group in the cache (for real-time updates)
 */
export function updateGroupInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  groupUlid: string,
  updates: Partial<ChatGroup>,
) {
  queryClient.setQueryData(
    queryKeys.messages.groups(),
    (oldData: ChatGroup[] | undefined) => {
      if (!oldData) return oldData;
      return oldData.map((group) =>
        group.ulid === groupUlid ? { ...group, ...updates } : group,
      );
    },
  );
}

/**
 * Add a new chat group to the cache (for newly created groups)
 * Adds to the beginning of the list and deduplicates
 */
export function addGroupToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  newGroup: ChatGroup,
) {
  queryClient.setQueryData(
    queryKeys.messages.groups(),
    (oldData: ChatGroup[] | undefined) => {
      // Filter out any existing group with same ulid (dedupe)
      const existingGroups = (oldData || []).filter(
        (g) => g.ulid !== newGroup.ulid,
      );
      // Add new group at the beginning
      return [newGroup, ...existingGroups];
    },
  );
}
